import { withDefaultSeatLimit } from '@helpers/seatLimit';
import { accountService } from '@services/domain-factories/account';
import { createStoreWithSelector } from '@utils/zustand/createStoreWithSelector';
import { createUseStore } from '@utils/zustand/createUseStore';
import { initOnce } from '@utils/zustand/initOnce';
import type {
  EntityScope,
  GroupEntityScope,
  Role,
  TenantScope
} from '@vincerosoft/assessfluence-types';
import {
  Entity,
  EntitySettings,
  GroupEntity,
  Member,
  ResourceGrant,
  UserGroup
} from '@vincerosoft/assessfluence-types';
import { TenantAccount } from '@vincerosoft/assessfluence-types/build/src/types/tenant/account';
import type { QueryConstraint } from 'firebase/firestore';

export type AccountContext = { scopeQuery: QueryConstraint } & (
  | {
      scope: 'standalone';
      account: Entity;
      accountHolder: TenantAccount;
      accountSettings: EntitySettings;
      resourceGrants: { [key in 'forms' | 'formGroups']: ResourceGrant[] };
      docScope: EntityScope;
    }
  | {
      scope: 'group';
      account: GroupEntity;
      accountHolder: TenantAccount;
      accountSettings: EntitySettings;
      resourceGrants?: null;
      docScope: GroupEntityScope;
    }
  | {
      scope: 'linked';
      account: Entity;
      accountHolder: GroupEntity;
      accountSettings: EntitySettings;
      resourceGrants: { [key in 'forms' | 'formGroups']: ResourceGrant[] };
      docScope: EntityScope;
    }
  | {
      scope: 'tenant';
      account: TenantAccount;
      accountHolder?: null;
      accountSettings?: null;
      resourceGrants?: null;
      docScope: TenantScope;
    }
);

type State = AccountContext & {
  isInitialized: boolean;
  loading: boolean;
  inFlightInit: Promise<void> | null;
  resetToken: number;
};

const initialValue = {
  accountHolder: null,
  account: null as unknown as AccountContext['account'],
  isInitialized: false,
  loading: false,
  inFlightInit: null,
  resetToken: 0,
  scope: null as unknown as AccountContext['scope'],
  resourceGrants: null,
  accountSettings: null
} as State;

type Actions = {
  initAccountContext: (
    member: Member,
    userType: Role['user_type'],
    userGroups: UserGroup[] | null
  ) => Promise<void>;
  handleAccountChange: (change: Entity | GroupEntity) => void;
  reset: () => void;
};

export const accountStore = createStoreWithSelector<Actions & State>()((set, get) => ({
  ...initialValue,
  resetToken: 0,

  reset: () => set((state) => ({ ...initialValue, resetToken: state.resetToken + 1 })),

  initAccountContext: (...args) =>
    initOnce(
      'isInitialized',
      set,
      get,
      () => accountService.getContext(...args),
      (context) => {
        set({
          ...context,
          isInitialized: true
        });
      }
    ),

  handleAccountChange: (change) => {
    set({
      account: withDefaultSeatLimit(change)
    } as State);
  }
}));

export const useAccountStore = createUseStore(accountStore);
